// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';

import type {
  Navigation,
  NavigationHeading,
  NavigationLink,
  NavigationProduct,
  NavigationTopic,
  NavigationNode,
} from 'docs/data';

import type {
  NavigationRequestModel,
  NavigationTopicRequestModel,
  NavigationSectionRequestModel,
  NavigationNodeRequestModel,
  NavigationNodeRequestModelTranslationStatus,
} from 'docs/types';

const messages = defineMessages({
  home: {
    id: 'docsShell.navigation.home',
    description: 'Home label',
    defaultMessage: 'Home',
  },
});

const transformHeading = (
  input: NavigationNodeRequestModel,
): NavigationHeading => ({
  type: 'heading',
  text: input.text,
  translated: null, // Headings never need the translated label
  xray: input.xray,
});

const transformTranslationStatus = (
  status: ?NavigationNodeRequestModelTranslationStatus,
): boolean | null => {
  if (status === 'translated') {
    return true;
  } else if (status === 'pending_translation') {
    return false;
  } else {
    return null;
  }
};

const transformLink = (input: NavigationNodeRequestModel): NavigationLink => ({
  type: 'link',
  text: input.text,
  href: input.route || '',
  description: input.hover || null,
  icon: null,
  // eslint-disable-next-line no-use-before-define
  children: input.children ? input.children.map(transformNode) : [],
  hidden: input.hidden || false,
  // These attributes are set by the client.
  active: false,
  expanded: false,
  selected: false,
  translated: transformTranslationStatus(input.translated), // TODO(adreyfus): Update docs-srv components to use new enum to render badge
  xray: input.xray,
});

const transformNode = (input: NavigationNodeRequestModel): NavigationNode =>
  input.type === 'header' ? transformHeading(input) : transformLink(input);

const createProduct = (
  section: NavigationSectionRequestModel,
  topic: NavigationTopicRequestModel,
): NavigationProduct => ({
  text: section.title,
  icon: section.product || null,
  href: section.route || '',
  topic: {
    text: topic.text || <FormattedMessage {...messages.home} />,
    href: topic.href,
    translated: null,
  },
  children: section.items.map(transformNode) || [],
  active: false,
  hidden: false,
  translated: null,
  xray: section.xray,
});

const transformProducts = (
  input: NavigationRequestModel,
): Array<NavigationProduct> => {
  return input.topics.reduce((products, currTopic) => {
    return [
      ...products,
      ...currTopic.sections.map((s) => createProduct(s, currTopic)),
    ];
  }, []);
};

const transformTopic = (
  input: NavigationTopicRequestModel,
): NavigationTopic => ({
  text: input.text,
  href: input.href,
  active: false,
  sections: input.sections.length ? input.sections.map((s) => s.title) : [],
  translated: null,
  xray: input.xray,
});

export const transformNavigation = (
  input: NavigationRequestModel,
): Navigation => {
  return {
    version: 'modern',
    topics: input.topics.map(transformTopic),
    products: transformProducts(input),
    open: true,
  };
};
